export const TASK_TYPES = ["daily", "sweet", "challenge", "surprise"] as const;

/** 四个分类的展示信息。法语名用在板块标题和任务卡标签上。 */
export const TASK_TYPE_META = {
  daily: { fr: "Le Quotidien", zh: "日常", accent: "navy" },
  sweet: { fr: "La Douceur", zh: "甜蜜", accent: "vermillion" },
  challenge: { fr: "Le Défi", zh: "挑战", accent: "amber" },
  surprise: { fr: "La Surprise", zh: "隐藏", accent: "sage" }
} as const satisfies Record<
  (typeof TASK_TYPES)[number],
  { fr: string; zh: string; accent: string }
>;
export const TASK_DIFFICULTIES = ["easy", "medium", "hard"] as const;
export const TASK_STATUSES = [
  "pending",
  "submitted",
  "completed",
  "failed",
  "expired",
  "cancelled"
] as const;
export const VERIFICATION_MODES = ["self", "ai_review"] as const;
export const PROOF_REQUIREMENTS = [
  "none",
  "text",
  "image",
  "text_or_image",
  "text_and_image"
] as const;
export const AI_PROOF_REQUIREMENTS = [
  "none",
  "text",
  "text_and_image"
] as const;
export const RECURRENCE_MODES = ["once", "daily"] as const;
export const REVEAL_MODES = ["immediate", "next_visit", "at_time"] as const;
export const ACTORS = ["AI", "user", "system"] as const;

/** Le Coffret · 盲盒稀有度（spec P2）。顺序即由常见到罕见，UI 按这个排。 */
export const RARITIES = ["commun", "rare", "precieux", "legendaire"] as const;

export const RARITY_META = {
  commun: { fr: "Commun", zh: "普通", accent: "sage" },
  rare: { fr: "Rare", zh: "稀有", accent: "blue" },
  precieux: { fr: "Précieux", zh: "珍贵", accent: "gold" },
  legendaire: { fr: "Légendaire", zh: "传说", accent: "coral" }
} as const satisfies Record<(typeof RARITIES)[number], { fr: string; zh: string; accent: string }>;

/** 一次抽奖最多消耗几张券，同时也是 pool.ticket_cost 的上限。 */
export const MAX_TICKETS_PER_DRAW = 10;

/** Les Promesses · 承诺追踪（spec 第六章）。 */
export const PROMISE_OWNERS = ["user", "AI"] as const;
export const PROMISE_STATUSES = ["open", "kept", "broken", "cancelled"] as const;

/**
 * 承诺的两道时间线（spec 6.2）。两个数都是「距上一次进度留痕过了几天」，
 * 不是距创建时间——她汇报过一次，钟就从那次重新走。
 */
export const PROMISE_NUDGE_DAYS = 3;
export const PROMISE_REPORT_DAYS = 7;

/**
 * 留痕。批注区、承诺进度、任务评语共用这一套写法——
 * 加加把批注区和承诺归成一组时说的就是这个意思。
 */
export const ANNOTATION_SCOPES = ["daily", "task", "promise"] as const;

/** 克部長の夜间指令（spec 5.3）。不消耗积分，只有状态。 */
export const NIGHT_DIRECTIVE_STATUSES = [
  "active",
  "acknowledged",
  "completed",
  "released",
  "expired"
] as const;

/** 克部長の特権（spec 5.2）。行使一次的三种归宿。 */
export const PRIVILEGE_USE_STATUSES = ["active", "completed", "cancelled"] as const;

/** 隐藏任务每日掉落概率，百分数（spec 2.3 建议 60%）。 */
export const DEFAULT_SURPRISE_DROP_RATE = 60;

/** 掉落任务的默认时效：掉出来之后多少小时内要完成（spec 2.3）。 */
export const DEFAULT_SURPRISE_TTL_HOURS = 24;

/** 掉落生成的任务在 tasks.source 上的标记。免罚判定靠它，别改。 */
export const SURPRISE_DROP_SOURCE = "surprise_drop";

export const TIMEZONE_OPTIONS = [
  { value: "Asia/Shanghai", label: "中国标准时间 · 上海" },
  { value: "Asia/Tokyo", label: "日本标准时间 · 东京" },
  { value: "Asia/Seoul", label: "韩国标准时间 · 首尔" },
  { value: "Asia/Singapore", label: "新加坡时间" },
  { value: "Europe/London", label: "英国时间 · 伦敦" },
  { value: "Europe/Paris", label: "中欧时间 · 巴黎" },
  { value: "America/New_York", label: "美国东部时间 · 纽约" },
  { value: "America/Los_Angeles", label: "美国西部时间 · 洛杉矶" },
  { value: "Australia/Sydney", label: "澳大利亚东部时间 · 悉尼" }
] as const;

export const SUPPORTED_TIMEZONES = TIMEZONE_OPTIONS.map((option) => option.value);
export type SupportedTimezone = (typeof TIMEZONE_OPTIONS)[number]["value"];

export const DIFFICULTY_MULTIPLIER = {
  easy: 1,
  medium: 2,
  hard: 3
} as const;

export const MAX_PROOF_IMAGES = 4;
export const MAX_PROOF_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_PROOF_IMAGE_PIXELS = 24_000_000;

/**
 * Streak 加成是当日任务积分的百分比（spec 3.1），不是固定分数。
 * 上游 Phosphene 用的是 1/2/3 分的小额固定加成，跟这里 30-3000 分的量纲对不上。
 */
export function streakBonusRate(streak: number): number {
  if (streak >= 30) return 1;
  if (streak >= 14) return 0.5;
  if (streak >= 7) return 0.25;
  if (streak >= 3) return 0.1;
  return 0;
}

/** 当日任务得分 × streak 费率，四舍五入到整分。 */
export function streakBonusForDay(streak: number, dayTaskPoints: number): number {
  if (dayTaskPoints <= 0) return 0;
  return Math.round(dayTaskPoints * streakBonusRate(streak));
}
